/**
 * Phần thuần của màn Danh sách Project và Danh sách luồng (`RpaProjectsView`, `RpaWorkflowsView`,
 * `WorkflowDetailTabs`, `WorkflowRunButton` của web-portal native): gắn Project cho Workflow, tìm, lọc, nhóm theo
 * loại, giải Workflow con, kết quả lượt chạy đang theo dõi.
 */
import type {
  BuocSoDo,
  LoaiWorkflow,
  LuotChay,
  LuotGanNhat,
  ProjectOpenRpa,
  WorkflowOpenCore,
} from '../types.ts';

export const LOAI_WORKFLOW: readonly LoaiWorkflow[] = ['openrpa', 'script'];
/** Giá trị ô lọc Project cho Workflow không thuộc Project nào. */
export const KHONG_GAN = '__chua-gan';

/** Mọi mục trong `haystack` nối lại có chứa `q` không (không phân biệt hoa thường; `q` trống là khớp). */
export function coChua(haystack: readonly unknown[], q: string): boolean {
  const tim = q.trim().toLowerCase();
  return !tim || haystack.map((x) => (x == null ? '' : String(x))).join(' ').toLowerCase().includes(tim);
}

// ─────────────── Project ───────────────

export function tongWorkflow(projects: readonly ProjectOpenRpa[]): number {
  return projects.reduce((n, p) => n + p.workflows.length, 0);
}

/** Tìm theo tên, id Project và tên Workflow bên trong. */
export function locProject(projects: readonly ProjectOpenRpa[], tim: string): ProjectOpenRpa[] {
  return projects.filter((p) => coChua([p.name, p.id, ...p.workflows.map((w) => w.name)], tim));
}

/** Dòng CSV của danh sách Project: tên, id, Workflow nối `; `, cập nhật (đã định dạng bởi phía gọi). */
export function dongCsvProject(projects: readonly ProjectOpenRpa[], ngay: (iso: string | null) => string): string[][] {
  return projects.map((p) => [p.name, p.id, p.workflows.map((w) => w.name).join('; '), ngay(p.updatedAt)]);
}

// ─────────────── Workflow ───────────────

export interface WorkflowCoProject extends WorkflowOpenCore {
  projectId: string;
  projectName: string;
}

export const loaiCua = (w: { kind?: LoaiWorkflow }): LoaiWorkflow => w.kind ?? 'openrpa';

export function ganProject(workflows: readonly WorkflowOpenCore[], projects: readonly ProjectOpenRpa[]): WorkflowCoProject[] {
  const chu = new Map(projects.flatMap((p) => p.workflows.map((w) => [w.id, p] as const)));
  return workflows.map((w) => ({ ...w, projectId: chu.get(w.id)?.id ?? '', projectName: chu.get(w.id)?.name ?? '' }));
}

/** `project` rỗng = mọi Project; `KHONG_GAN` = Workflow không thuộc Project nào. Tìm theo id, tên, tên Project. */
export function locWorkflow(items: readonly WorkflowCoProject[], loc: { tim: string; project: string }): WorkflowCoProject[] {
  return items.filter(
    (w) =>
      (!loc.project || (loc.project === KHONG_GAN ? !w.projectId : w.projectId === loc.project)) &&
      coChua([w.id, w.name, w.projectName], loc.tim),
  );
}

export function demTheoLoai(items: readonly { kind?: LoaiWorkflow }[]): Record<LoaiWorkflow, number> {
  const ra: Record<LoaiWorkflow, number> = { openrpa: 0, script: 0 };
  for (const w of items) ra[loaiCua(w)] += 1;
  return ra;
}

/** Workflow đang xem: id đã chọn nếu còn trong danh sách, không thì Workflow đầu tiên; rỗng khi không có. */
export function workflowDangXem(items: readonly { id: string }[], daChon: string): string {
  return items.some((w) => w.id === daChon) ? daChon : (items[0]?.id ?? '');
}

/** Các bước thật của Workflow: bỏ ô Bắt đầu / Kết thúc. */
export function cacBuoc(nodes: readonly BuocSoDo[]): BuocSoDo[] {
  return nodes.filter((n) => n.kind !== 'start' && n.kind !== 'end');
}

/** Workflow con được gọi (`target` của InvokeOpenRPA), không trùng, theo thứ tự xuất hiện. */
export function workflowCon(nodes: readonly BuocSoDo[]): string[] {
  return [...new Set(cacBuoc(nodes).map((n) => n.target).filter((t): t is string => Boolean(t)))];
}

/** InvokeOpenRPA trỏ tới Workflow con bằng `_id` hoặc `Project/Tên`. Trả id của Workflow đó, không thấy thì `undefined`. */
export function giaiWorkflowCon(
  target: string,
  items: readonly { id: string }[],
  projects: readonly ProjectOpenRpa[],
): string | undefined {
  if (items.some((w) => w.id === target)) return target;
  for (const p of projects) {
    const w = p.workflows.find((x) => `${p.name}/${x.name}` === target);
    if (w) return w.id;
  }
  return undefined;
}

// ─────────────── Chạy Workflow ───────────────

/** Mã lượt chạy vừa tạo: hợp đồng gọi là `runCode`, `raDay` của backend đặt nó ở `id` và `runId`. */
export function maLuot(luot: LuotChay): string {
  return luot.runCode || luot.runId || luot.id || '';
}

export type KetQuaLuot = 'xong' | 'matDau' | 'loi';

/**
 * Lượt mình vừa bấm chạy đã kết thúc chưa và ra sao. `null` = chưa kết thúc, hoặc lượt gần nhất không phải lượt đang
 * theo dõi. Lượt đã thôi "active" mà trạng thái vẫn đang chạy là máy chủ mất dấu (quá thời gian chờ Robot).
 */
export function ketQuaLuot(dangTheoDoi: string, ganNhat: LuotGanNhat | undefined): KetQuaLuot | null {
  const run = ganNhat?.run;
  if (!dangTheoDoi || !ganNhat || ganNhat.active || !run || run.id !== dangTheoDoi) return null;
  if (run.status === 'success') return 'xong';
  if (run.status === 'running' || run.status === 'queued' || run.status === 'retrying') return 'matDau';
  return 'loi';
}
